import { useEffect, useRef, useState } from 'react';
import { Box, SxProps } from '@mui/material';
import { makeStyles } from '@mui/styles';
import clsx from 'clsx';
import Chartist from 'chartist';
import 'chartist-plugin-axistitle';
import { MasonTheme } from '@bymason-private/controller-v1-theme-augmentation';
import { ChartProps, LineChartComp, LineChartOptions, LineChartResponsiveOptions } from './types';
import { addPlugins, createEventHandlers, disposeEventHandlers, useStylesBase } from './logic';

export type LineChartProps = ChartProps<LineChartOptions, LineChartResponsiveOptions> & {
  sx?: SxProps;
};

const useStyles = makeStyles((theme: MasonTheme) => ({
  root: {
    '& .ct-point': {
      strokeWidth: 0,
      transition: `stroke-width ${theme.transitions.duration.short}ms ${theme.transitions.easing.easeIn}`,
    },
    '& .ct-area': {
      fillOpacity: 0.15,
    },
    '& .ct-label.ct-vertical': {
      whiteSpace: 'nowrap',
    },
    '& .ct-series-a .ct-area': {
      fill: '#625FFF',
    },
    '& .ct-series-a .ct-line': {
      stroke: '#625FFF',
    },
    '& .ct-series-a .ct-point': {
      stroke: '#453BB4',
    },
    '&:hover .ct-point': {
      strokeWidth: theme.spacing(1.25),
    },
  },
}));

export function LineChart({
  className,
  sx,
  data,
  options,
  responsiveOptions,
  onCreated,
  onDraw,
}: LineChartProps) {
  const classes = { ...useStylesBase(), ...useStyles() };
  const chartNode = useRef(null);
  const [chart, setChart] = useState<LineChartComp | null>(null);

  //
  // handle creation/update of chart
  //
  useEffect(() => {
    if (!chartNode.current) {
      return;
    }

    addPlugins(options);

    if (chart) {
      chart.update(data, options, true);
    } else {
      const lineChart = new Chartist.Line(chartNode.current, data, options, responsiveOptions);

      setChart(lineChart);
    }
  }, [chart, data, options, responsiveOptions]);

  //
  // handle listener subscription/clean-up
  //
  useEffect(() => {
    createEventHandlers(chart, onCreated, onDraw);

    return () => {
      disposeEventHandlers(chart, onCreated, onDraw);
    };
  }, [chart, onCreated, onDraw]);

  return (
    <Box sx={sx} className={clsx('ct-chart', classes.chartBase, classes.root, className)} ref={chartNode} />
  );
}
